import { matchPath } from 'react-router-dom';
import { ROUTES } from 'utils/constants';

/**
 * Destination chunks the native app can land on via `?redirect=`.
 *
 * Without this, the destination's lazy chunk isn't requested until `navigate()`
 * runs — i.e. after the bridge token refresh and `CreateSession` have both
 * completed. Kicking the import off at mount overlaps the chunk download with
 * that otherwise-idle network time.
 *
 * Specifiers must match `App.tsx`'s `React.lazy` imports exactly, otherwise
 * webpack emits a second chunk instead of reusing the route's.
 */
const APP_ENTRY_CHUNKS: Record<string, () => Promise<unknown>> = {
  // The app opens /addCameraInfo for hubs too, and the entry redirects them
  // to /addHubInfo — so warm the hub flow as well, or a hub pairing waits on
  // that download after the redirect.
  [ROUTES.ADD_CAMERA_INFO]: () =>
    Promise.all([
      import('pages/v1/AddCameraInfoEntry'),
      import('pages/v1/AddHubInfoV1')
    ]),
  [ROUTES.ADD_HUB_INFO]: () => import('pages/v1/AddHubInfoV1'),

  // Both the deep-linked path and the canonical one it redirects to, so the
  // chunk is warm either way.
  [ROUTES.BUY_SUBSCRIPTION]: () => import('pages/v1/HomeBuySubscriptionV1'),
  [ROUTES.HOME_BUY_SUBSCRIPTION]: () =>
    import('pages/v1/HomeBuySubscriptionV1'),
  [ROUTES.SUBSCRIPTION_LIST]: () =>
    import('pages/v1/HomeSubscriptionsDetailsV1/HomeSubscriptionListV1'),
  [ROUTES.SUBSCRIPTION_UPGRADE]: () =>
    import('pages/v1/HomeUpgradeSubscriptionV1'),
  [ROUTES.HOME_SUBSCRIPTION_UPGRADE]: () =>
    import('pages/v1/HomeUpgradeSubscriptionV1'),
  [ROUTES.HOME_ADD_PLAN]: () => import('pages/v1/HomeAddPlanV1'),
  [ROUTES.HOME_REMOVE_PLAN]: () => import('pages/v1/HomeRemovePlanV1'),

  [ROUTES.DATA_SUBSCRIPTION]: () =>
    import('pages/v1/HomeBuyDataSubscriptionV1'),
  [ROUTES.UPGRADE_DATA_SUBSCRIPTION]: () =>
    import('pages/v1/HomeUpgradeDataSubscriptionV1'),
  [ROUTES.ONE_TIME_DATA_PURCHASE]: () =>
    import('pages/v1/BuyOneTimeDataV1/index'),
  [ROUTES.DATA_AUTO_TOP_UP]: () => import('pages/v1/DataAutoChargeV1/index'),
  [ROUTES.DATA_AUTO_TOP_UP_PAYMENT]: () =>
    import('pages/v1/DataAutoTopUpPaymentV1/index'),

  [ROUTES.BIRD_BUY_SUBSCRIPTION]: () =>
    import('pages/v1/BuyBirdFeederSubscriptionV1'),
  [ROUTES.BIRD_SUBSCRIPTION_DETAIL]: () =>
    import('pages/v1/SubscriptionDetailsV1'),
  [ROUTES.BIRD_SUBSCRIPTION_UPGRADE]: () =>
    import('pages/v1/BirdFeederUpgradeSubscriptionV1'),

  [ROUTES.PET_BUY_SUBSCRIPTION]: () =>
    import('pages/v1/BuyPetCameraSubscriptionV1'),
  [ROUTES.PET_SUBSCRIPTION_DETAIL]: () =>
    import('pages/v1/SubscriptionDetailsV1'),
  [ROUTES.PET_SUBSCRIPTION_UPGRADE]: () =>
    import('pages/v1/PetCameraUpgradeSubscriptionV1'),

  [ROUTES.BABY_BUY_SUBSCRIPTION]: () =>
    import('pages/v1/BuyBabyCameraSubscriptionV1'),
  [ROUTES.BABY_SUBSCRIPTION_DETAIL]: () =>
    import('pages/v1/SubscriptionDetailsV1'),
  [ROUTES.BABY_SUBSCRIPTION_UPGRADE]: () =>
    import('pages/v1/BabyCameraUpgradeSubscriptionV1'),

  [ROUTES.TRAIL_CAM_BUY_SUBSCRIPTION]: () =>
    import('pages/v1/BuyTrailCameraSubscriptionV1'),
  [ROUTES.TRAIL_CAM_SUBSCRIPTION_DETAIL]: () =>
    import('pages/v1/SubscriptionDetailsV1'),
  [ROUTES.TRAIL_CAM_SUBSCRIPTION_UPGRADE]: () =>
    import('pages/v1/TrailCameraUpgradeSubscriptionV1'),

  [ROUTES.PAYMENT_METHODS]: () => import('pages/v1/PaymentMethodListingV1'),
  [ROUTES.RETRY_PAYMENT]: () => import('pages/v1/RetryPaymentV1'),
  [ROUTES.BILLING]: () => import('pages/v1/MyOrdersV1'),
  [ROUTES.BILLINGS]: () => import('pages/v1/MyOrdersV1')
};

/**
 * Warms the chunk for `?redirect=<path>`. Fire-and-forget: a failure here is
 * harmless because `React.lazy` re-imports on render and surfaces the error
 * through the route's error boundary.
 */
export function prefetchAppEntryChunk(redirectPath: string): void {
  if (!redirectPath) return;

  const pathname = `/${redirectPath.replace(/^\/+/, '')}`;
  const match = Object.entries(APP_ENTRY_CHUNKS).find(([pattern]) =>
    matchPath(pattern, pathname)
  );

  match?.[1]().catch(() => {});
}
